import React, { useState, useEffect, useContext } from 'react';
import { CirclePicker, HuePicker, SketchPicker } from 'react-color';
import HuePointer from './HuePointer/HuePointer';
import FormButtons from '../../form-control-buttons/FormButtons/FormButtons';
import { t } from '../../../../common/components/T';
import { LangContext } from '../../../../common/decorators/Language';
import FormInput from '../form-input/FormInput/FormInput';
import { useWidgetTranslates } from '../../../../common/utils/utils';
import PropTypes from 'prop-types';
import css from './FormColorPicker.module.scss';
import classnames from 'classnames';
import FromInputTitle from '../FormFieldTitle/FormFieldTitle';
import { generateCompStylers } from '../../../../common/utils/styles';
import { FieldError, FieldInfo } from '../../../../hybrid/wrappers';

const FormColorPicker = ({ buttonText, value, setFormData, attribute, info, errors, compStyle, title, hint, required }) => {
    const [val, setVal] = useState('var(--blueDeepColor))');
    const [showAnotherColor, setShowAnotherColor] = useState(false);
    const [anotherColor, setAnotherColor] = useState(['#58FF73']);
    const [showPicker, setShowPicker] = useState(false);
    const [newColor, setNewColor] = useState('#d0a4ff');
    const transl = useWidgetTranslates('form');
    const { translation } = useContext(LangContext) || {};
    const openAnotherColor = () => {
        setShowAnotherColor(true);
    };
    const openPicker = () => {
        setShowPicker(!showPicker);
    };
    useEffect(() => {
        value && setVal(value);
    }, [value]);
    const { stylers, classes } = generateCompStylers(compStyle);

    return (
        <div style={stylers.wrapper} className={classnames(css.formGroup, classes.wrapper)}>
            {!!title ? (
                <FromInputTitle
                    errors={errors}
                    title={title}
                    compStyle={compStyle}
                    hint={hint}
                    required={required}
                    variationStyle={'separated_title'}
                />
            ) : null}
            <div className={css.formColorPicker}>
                <div onClick={() => openPicker()} className={css.formColorPickerButton}>
                    {buttonText || t('FormColorPicker-ChooseColors', 'Выбрать цвет', 'widget', transl, 'form')}
                </div>
                <div className={css.formColorPickerCircle}>
                    <div
                        onClick={() => openPicker()}
                        style={{ backgroundColor: val }}
                        className={classnames(css.formColorPickerCircleItem)}
                    ></div>
                    {showPicker && (
                        <>
                            <div className={css.formColorPickerBackdrop} onClick={() => setShowPicker(false)}></div>
                            <div className={css.formColorPickerWrapper}>
                                <div className={css.formColorPickerTitle}>
                                    {t('FormColorPicker-BasicColors', 'Базовые цвета', 'widget', transl, 'form')}
                                </div>
                                <div className={css.formColorPickerContainer}>
                                    <CirclePicker
                                        onChange={(color) => {
                                            setVal(color.hex);
                                            setFormData(attribute, color.hex);
                                            setShowPicker(false);
                                        }}
                                        circleSpacing={8}
                                        circleSize={26}
                                    />
                                </div>
                                <div className={css.formColorPickerTitle}>
                                    {t('FormColorPicker-ElseColors', 'Другие цвета', 'widget', transl, 'form')}
                                </div>
                                <div className={css.formColorPickerContainer}>
                                    <CirclePicker
                                        colors={anotherColor}
                                        onChange={(color) => {
                                            setVal(color.hex);
                                            setFormData(attribute, color.hex);
                                            setShowPicker(false);
                                        }}
                                        circleSpacing={8}
                                        circleSize={26}
                                        width='fit-content'
                                    />
                                    <div>
                                        <div className={css.formColorPickerAddButton} onClick={() => openAnotherColor()}>
                                            +
                                        </div>
                                    </div>
                                </div>
                                {showAnotherColor && (
                                    <div>
                                        <div className={css.formColorPickerContainer}>
                                            <div className={css.formColorPickerHeader}>
                                                <div>
                                                    <div
                                                        style={{
                                                            backgroundColor: newColor
                                                        }}
                                                        className={classnames(css.formColorPickerCircleFooter)}
                                                    ></div>
                                                </div>
                                                <div className={css.formColorPickerTitle}>
                                                    {t('FormColorPicker-16Code', '16-ричный код', 'widget', transl, 'form')}
                                                </div>
                                                <div>
                                                    <FormInput
                                                        variation={'small'}
                                                        compStyle={{
                                                            wrapper: css.formColorPickerInputWrap,
                                                            input: { lineHeight: '1', height: '40px', padding: '9px' }
                                                        }}
                                                        value={newColor}
                                                        setFormData={(attribute, value) => {
                                                            setNewColor(value);
                                                        }}
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                        <div className={css.formColorPickerContainer}>
                                            <SketchPicker
                                                color={newColor}
                                                onChange={(color) => setNewColor(color.hex)}
                                                disableAlpha={false}
                                                width='100%'
                                            />
                                        </div>
                                        <div className={css.formColorPickerTitle}>
                                            {t('FormColorPicker-Shade', 'Оттенок', 'widget', transl, 'form')}
                                        </div>
                                        <div className={css.formColorPickerContainer}>
                                            <HuePicker
                                                color={newColor}
                                                width='100%'
                                                onChange={(color) => setNewColor(color.hex)}
                                                pointer={() => <HuePointer color={newColor} />}
                                            />
                                        </div>
                                        <FormButtons
                                            sendFormData={() => {
                                                const newAnotherColor = anotherColor;
                                                newAnotherColor.push(newColor);
                                                setAnotherColor(newAnotherColor);
                                                setShowAnotherColor(false);
                                            }}
                                            buttons={[
                                                {
                                                    type: 'cancel',
                                                    buttonText: t('cancel', 'Отмена', 'all', translation),
                                                    clickFunc: () => setShowAnotherColor(false)
                                                },
                                                { type: 'ok', buttonText: 'OK' }
                                            ]}
                                            customFormButton={{
                                                ok: {
                                                    singleButton: {
                                                        'max-width': '82px',
                                                        width: '82px'
                                                    }
                                                },
                                                cancel: {
                                                    singleButton: {
                                                        width: '118px',
                                                        'max-width': '118px'
                                                    }
                                                }
                                            }}
                                        />
                                    </div>
                                )}
                            </div>
                        </>
                    )}
                </div>
            </div>

            {!!info && <FieldInfo compStyle={compStyle?.info}>{info}</FieldInfo>}
            {errors && errors.length
                ? errors.map((err, idx) => (
                      <FieldError compStyle={compStyle?.error} key={`${attribute}${idx}`}>
                          {err}
                      </FieldError>
                  ))
                : null}
        </div>
    );
};
FormColorPicker.propTypes = {
    value: PropTypes.string,
    setFormData: PropTypes.func,
    attribute: PropTypes.string
};
export default FormColorPicker;
